/**
 * The ONE shared bank-line info-header. Every surface that shows a synced bank
 * line — the close-ledger row, that row's expanded detail, and the triage popup
 * — composes this so the three line-context signals never drift apart:
 *
 *   1. "Not yet cleared" — the line is still pending at the bank, so its amount
 *      or date may still shift when it posts. Display-only (ADR-071 #566): an
 *      Uncleared line classifies exactly like a settled one.
 *   2. duplicate hint    — a still-pending line that may duplicate a settled
 *      twin (#569). A hint, never an action; the treasurer deletes whichever
 *      copy is the duplicate.
 *   3. straggler tag     — an unclassified line dated into an already-closed
 *      (filed) reporting period (ADR-071 amendment #575): "Needs amendment".
 *
 * Before this header, "Not yet cleared" lived only in the popup while the
 * duplicate/straggler signals lived only on the ledger row, so a pending line
 * looked fully settled once opened from the ledger. Composing one component
 * everywhere means a signal added here appears on all three surfaces at once.
 *
 * `surface` scopes each signal's testid so the collapsed row and its expanded
 * detail — both in the DOM at the same time — stay independently addressable.
 * The header renders nothing when the line carries no signal, so callers can
 * mount it unconditionally.
 */
import {
  BankTransactionClearingStatus,
  type SortInboxBankTransactionDto,
} from '@signalvote/shared';
import { formatAmount } from '@/components/finance/utils';

/**
 * The "Not yet cleared" badge and the lingering-duplicate hint are hidden for
 * now. Flip to true to bring both back on every surface.
 */
const SHOW_PENDING_SIGNALS = false;

/** Which surface is composing the header — scopes the signal testids. */
export type BankLineHeaderSurface = 'row' | 'detail' | 'popup';

export interface BankLineInfoHeaderProps {
  /** The TriageItem id — scopes the signal testids to this line. */
  id: string;
  bt: SortInboxBankTransactionDto;
  surface: BankLineHeaderSurface;
  /** Per-surface spacing (e.g. `mt-2` in the popup, `mb-0.5` in the row). */
  className?: string;
}

export function BankLineInfoHeader({
  id,
  bt,
  surface,
  className,
}: BankLineInfoHeaderProps) {
  const isStraggler = !!bt.inClosedPeriod;
  const isUncleared =
    SHOW_PENDING_SIGNALS &&
    bt.clearingStatus === BankTransactionClearingStatus.Uncleared;
  const duplicateHint = SHOW_PENDING_SIGNALS ? (bt.duplicateHint ?? null) : null;

  if (!isStraggler && !isUncleared && !duplicateHint) return null;

  return (
    <div
      data-testid={`bank-line-info-header-${surface}-${id}`}
      className={`flex flex-col items-start gap-1${className ? ` ${className}` : ''}`}
    >
      {/* ADR-071 amendment (#575) — the straggler's "Needs amendment" mono tag.
          Derived (bt.inClosedPeriod), never stored. The jurisdiction-aware
          "how to amend" guidance stays a separate, richer element in the
          expanded detail; this is the at-a-glance marker. */}
      {isStraggler && (
        <span
          data-testid={`bank-line-straggler-${surface}-${id}`}
          className="block w-fit font-mono text-[9.5px] tracking-[.04em] text-amber-600"
        >
          Needs amendment
        </span>
      )}
      {/* ADR-071 pending-ingestion (#566) — mark a not-yet-settled line so the
          treasurer knows its figures may still shift as it posts. */}
      {isUncleared && (
        <span
          data-testid={`bank-line-uncleared-${surface}-${id}`}
          title="This bank line has not cleared the bank yet; its amount or date may still change when it posts."
          className="inline-flex w-fit items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300"
        >
          Not yet cleared
        </span>
      )}
      {/* #569 — the lingering-duplicate hint on a still-pending line that may
          duplicate a settled twin. Two lines: the concern (naming the settled
          line's amount + date) and the low-friction resolution (delete the
          duplicate copy; we never merge). Kept fully visible (not a tooltip) so
          it stays legible at font-scale 1.3. The amber tint (not the primary
          blue) matches the "Not yet cleared" badge — a caution, not a
          suggestion. */}
      {duplicateHint && (
        <div
          data-testid={`bank-line-duplicate-hint-${surface}-${id}`}
          className="w-fit max-w-prose rounded-md bg-amber-100 px-2 py-1 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-300"
        >
          <p className="font-semibold">
            This may duplicate a line that already cleared —{' '}
            {formatAmount(duplicateHint.amount)} on {duplicateHint.settledDate}.
          </p>
          <p className="mt-0.5 font-normal text-amber-800/80 dark:text-amber-300/80">
            If it's the same charge, delete whichever copy is the duplicate — we
            never merge them for you.
          </p>
        </div>
      )}
    </div>
  );
}
